import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  Box,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import useStyles from "../../../../styles";
import { toast } from "react-toastify";
import axios from "axios";
import socket from "../../../socket/socket";

function IitRestartButtonPopup({
  open,
  onClose,
  onSubmit,
  rowId,
  selectedLearnerId,
  decryptedToken,
  setChangeStatus,
}) {
  const classes = useStyles();
  const [step, setStep] = useState(1);
  const [option, setOption] = useState("");
  const [customTime, setCustomTime] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(1);
      setOption("");
      setCustomTime("");
      setPassword("");
      setShowPassword(false);
    }
  }, [open]);

  const handleOptionChange = (event) => {
    setOption(event.target.value);
    setCustomTime("");
  };

  const handleToggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClose = () => {
    setStep(1);
    setOption("");
    setCustomTime("");
    setPassword("");
    setShowPassword(false);
    onClose();
  };

  const handleNextStep = () => {
    if (!option) {
      toast.error("Please select an option.");
      return;
    }
    if (option === "custom_time" && !customTime.toString().trim()) {
      toast.error("Please enter custom time.");
      return;
    }
    setStep(2);
  };

  const handleSubmit = async () => {
    if (!password) {
      toast.error("Please enter a password.");
      return;
    }

    try {
      setLoading(true);

      // Step 1: Password Check
      const checkPasswordData = { user: { password } };
      const passwordCheckResponse = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/check/password`,
        checkPasswordData,
        { headers: { Authorization: `Bearer ${decryptedToken}` } }
      );

      if (!passwordCheckResponse.data.success) {
        toast.error("Invalid password. Please try again.");
        setLoading(false);
        return;
      }

      // Step 2: Resume API call
      if (onSubmit) {
        await onSubmit({ option, customTime, selectedLearnerId, password });
      } else {
        const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/live/resume/all`;

        const learnerData = {
          ids: [Number(selectedLearnerId)],
          exam_id: Number(rowId),
        };

        if (option === "custom_time") {
          learnerData.remaining_time = customTime;
        }

        const requestBody = {
          learner: learnerData,
        };

        const response = await axios.put(apiUrl, requestBody, {
          headers: {
            Authorization: `Bearer ${decryptedToken}`,
          },
        });

        socket.emit("relogin", { examId: rowId, userId: selectedLearnerId });

        if (setChangeStatus) {
          setChangeStatus((prev) => {
            const existingIndex = prev.findIndex(
              (item) =>
                Number(item.userId) === Number(selectedLearnerId) &&
                Number(item.examId) === Number(rowId)
            );

            if (existingIndex >= 0) {
              const updated = [...prev];
              updated[existingIndex] = {
                ...updated[existingIndex],
                status: "Incomplete",
              };
              return updated;
            }

            return [
              ...prev,
              {
                examId: Number(rowId),
                userId: Number(selectedLearnerId),
                status: "Incomplete",
              },
            ];
          });
        }

        toast.success(response.data?.message || "Exam restarted successfully!");
      }
      handleClose();
    } catch (error) {
      console.error("Error restarting exam:", error);
      toast.error(
        error.response?.data?.message || "Invalid password. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            variant="h6"
            className={`${classes.fontfamilyoutfit} ${classes.fw500}`}
            style={{ fontWeight: 600 }}
          >
            {step === 1 ? "Start Again" : "Enter Password"}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {step === 1 ? (
          <FormControl component="fieldset" style={{ width: "100%" }}>
            <FormLabel
              component="legend"
              style={{ marginBottom: "12px", fontWeight: 500, color: "#333" }}
            >
              Select Option:
            </FormLabel>
            <RadioGroup value={option} onChange={handleOptionChange}>
              <FormControlLabel
                value="last_attempt_time"
                control={<Radio color="primary" />}
                label="Last Attempt Time"
              />
              <FormControlLabel
                value="custom_time"
                control={<Radio color="primary" />}
                label="Custom Time"
              />
            </RadioGroup>

            {option === "custom_time" && (
              <Box marginTop={2}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Custom Time (in Seconds)"
                  placeholder="Enter custom time in Seconds"
                  value={customTime}
                  onChange={(e) => setCustomTime(e.target.value)}
                  size="small"
                />
              </Box>
            )}
          </FormControl>
        ) : (
          <Box style={{ width: "100%" }}>
            <Typography variant="body2" style={{ marginBottom: "12px", color: "#555" }}>
              Please enter your password to confirm exam restart:
            </Typography>
            <TextField
              fullWidth
              type={showPassword ? "text" : "password"}
              variant="outlined"
              placeholder="Enter Your Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleToggleShowPassword} edge="end" size="small">
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions style={{ padding: "16px 24px" }}>
        {step === 1 ? (
          <>
            <Button onClick={handleClose} color="secondary" variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={handleNextStep}
              color="primary"
              variant="contained"
              className={classes.custombtnblue}
            >
              Next
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => setStep(1)} color="secondary" variant="outlined">
              Back
            </Button>
            <Button
              onClick={handleSubmit}
              color="primary"
              variant="contained"
              disabled={loading}
              className={classes.custombtnblue}
            >
              {loading ? "Submitting..." : "Submit"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default IitRestartButtonPopup;
